import React, { useCallback, useEffect, useRef, useState } from "react";
import { CaretDown } from "@phosphor-icons/react";

const NAVY = "#182848";
const SLATE = "#4A5A72";
const ORANGE = "#EE7623";

// "NEW" badge on Fresh Start for the first 60 days after launch.
const FRESH_START_LAUNCH = new Date("2026-10-10T00:00:00Z");
export const isFreshStartNew = () =>
  Date.now() - FRESH_START_LAUNCH.getTime() < 60 * 24 * 60 * 60 * 1000;

// Shared "Services" menu — mirrored in the inline markup of the static funnel pages (public/*/index.html).
// Labels are display text only; every href is an existing route.
export const NAV_GROUPS = [
  {
    heading: "Cleaning",
    testid: "nav-group-cleaning",
    links: [
      { href: "/deep-cleaning", label: "Deep Cleaning", testid: "nav-cleaning-deep" },
      { href: "/carpet-cleaning", label: "Carpet Cleaning", testid: "nav-cleaning-carpet" },
      { href: "/move-out-cleaning", label: "Move-Out Cleaning", testid: "nav-cleaning-move-out" },
      { href: "/same-day-cleaning", label: "Same-Day Cleaning", testid: "nav-cleaning-same-day" },
      { href: "/cleaning", label: "All Cleaning Services", testid: "nav-cleaning-all" },
    ],
  },
  {
    heading: "Cleanouts & Junk",
    testid: "nav-group-cleanouts",
    links: [
      { href: "/junk-removal", label: "Junk Removal", testid: "nav-cleaning-junk-removal" },
      { href: "/fresh-start", label: "Whole House Cleanout", testid: "nav-cleaning-fresh-start", isNew: true },
      { href: "/hoarding-cleanup", label: "Hoarding Cleanup", testid: "nav-cleaning-hoarding-cleanup" },
      { href: "/basement-garage-cleanout", label: "Basement & Garage", testid: "nav-cleaning-basement-garage" },
      { href: "/estate-cleanout", label: "Estate Cleanouts", testid: "nav-cleaning-estate-cleanout" },
    ],
  },
  {
    heading: "For Landlords & Property Managers",
    testid: "nav-group-landlords",
    links: [
      { href: "/landlord-turnover", label: "Rental Turnovers", testid: "nav-cleaning-landlord-turnover" },
      { href: "/property-managers", label: "Property Manager Services", testid: "nav-property-managers-menu" },
    ],
  },
];

export const UTILITY_LINKS = [
  { href: "/service-area", label: "Service Area", testid: "nav-util-service-area" },
  { href: "/#how-it-works", label: "How It Works", testid: "nav-util-how-it-works" },
  { href: "/digital", label: "Digital Services", testid: "nav-services-digital" },
];

export const CLEANING_LINKS = [...NAV_GROUPS.flatMap((g) => g.links), ...UTILITY_LINKS];

export const NewBadge = () => (
  <span
    data-testid="nav-fresh-start-new-badge"
    className="ml-2 rounded-sm px-1.5 py-0.5 font-mono-label text-[9px] font-bold tracking-[0.18em]"
    style={{ backgroundColor: "#C84B31", color: "#F5F4F0" }}
  >
    NEW
  </span>
);

const GroupHeading = ({ children, testid }) => (
  <div
    role="presentation"
    data-testid={testid}
    className="mb-1 flex items-center gap-2 px-1 pt-1 font-mono-label text-[9.5px] font-bold tracking-[0.22em]"
    style={{ color: SLATE }}
  >
    <span aria-hidden="true" className="inline-block h-[2px] w-4 shrink-0" style={{ backgroundColor: ORANGE }} />
    <span className="uppercase">{children}</span>
  </div>
);

const usePath = () => (typeof window !== "undefined" ? window.location.pathname.replace(/\/+$/, "") || "/" : "");

// Desktop dropdown: hover + click + keyboard, 200ms close delay, parent is a toggle (not a link).
export const CleaningNavDropdown = ({ linkClass, align = "left" }) => {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);
  const hoverOpened = useRef(false);
  const btnRef = useRef(null);
  const path = usePath();
  const itemRefs = useRef([]);
  const N = CLEANING_LINKS.length;

  const cancelClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), 200);
  };
  useEffect(() => cancelClose, []);

  const focusItem = useCallback((i) => {
    const el = itemRefs.current[((i % N) + N) % N];
    if (el) el.focus();
  }, [N]);

  const onButtonKey = (e) => {
    if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setTimeout(() => focusItem(0), 0);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setOpen(true);
      setTimeout(() => focusItem(N - 1), 0);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const onItemKey = (e, i) => {
    if (e.key === "ArrowDown") { e.preventDefault(); focusItem(i + 1); }
    else if (e.key === "ArrowUp") { e.preventDefault(); focusItem(i - 1); }
    else if (e.key === "Home") { e.preventDefault(); focusItem(0); }
    else if (e.key === "End") { e.preventDefault(); focusItem(N - 1); }
    else if (e.key === "Escape") { e.preventDefault(); setOpen(false); btnRef.current?.focus(); }
    else if (e.key === "Tab") { setOpen(false); }
  };

  let idx = -1;
  const item = (l, extraClass = "") => {
    idx += 1;
    const i = idx;
    const current = l.href === path;
    return (
      <a
        key={l.href}
        ref={(el) => (itemRefs.current[i] = el)}
        href={l.href}
        role="menuitem"
        tabIndex={open ? 0 : -1}
        aria-current={current ? "page" : undefined}
        data-testid={l.testid}
        onKeyDown={(e) => onItemKey(e, i)}
        className={`flex min-h-[40px] items-center justify-between gap-3 rounded-[1px] px-2 py-2 text-[13px] outline-none transition-colors hover:bg-[#1B2A22] hover:text-[#F5F4F0] focus-visible:bg-[#1B2A22] focus-visible:text-[#F5F4F0] ${extraClass}`}
        style={{ color: current ? "#C84B31" : undefined }}
      >
        <span className="whitespace-nowrap">{l.label}</span>
        {l.isNew && isFreshStartNew() && <NewBadge />}
        {current && <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-[#C84B31]" />}
      </a>
    );
  };

  return (
    <div
      className="relative"
      onMouseEnter={() => { cancelClose(); if (!open) { setOpen(true); hoverOpened.current = true; } }}
      onMouseLeave={() => { hoverOpened.current = false; scheduleClose(); }}
      onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false); }}
      data-testid="nav-cleaning-dropdown"
    >
      <button
        ref={btnRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls="cleaning-menu"
        data-testid="nav-cleaning-toggle"
        onClick={() => { if (open && hoverOpened.current) { hoverOpened.current = false; return; } setOpen((v) => !v); }}
        onKeyDown={onButtonKey}
        className={`${linkClass} inline-flex items-center gap-1 bg-transparent`}
      >
        Services
        <CaretDown size={12} weight="bold" className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      <div
        id="cleaning-menu"
        role="menu"
        aria-label="Services"
        aria-hidden={!open}
        data-testid="nav-cleaning-menu"
        onMouseEnter={cancelClose}
        onMouseLeave={scheduleClose}
        className={`absolute top-full z-50 mt-2 w-max border ${align === "right" ? "right-0 origin-top-right" : "left-0 origin-top-left"} border-[#1C1A17]/15 bg-[#FBFAF6] p-3 shadow-[0_18px_40px_-16px_rgba(28,26,23,0.45)] transition-[opacity,transform] duration-150 ${
          open ? "pointer-events-auto scale-100 opacity-100" : "pointer-events-none scale-95 opacity-0"
        }`}
        style={{ color: NAVY }}
      >
        <div className="grid grid-cols-3 gap-x-6">
          {NAV_GROUPS.map((g) => (
            <div key={g.heading} className="min-w-[180px]" data-testid={g.testid}>
              <GroupHeading testid={`${g.testid}-heading`}>{g.heading}</GroupHeading>
              {g.links.map((l) => item(l, "font-medium"))}
            </div>
          ))}
        </div>
        <div className="mt-2 flex items-center gap-1 border-t border-[#1C1A17]/10 pt-2" data-testid="nav-utility-row">
          {UTILITY_LINKS.map((l) => item(l, "text-[12px] font-normal opacity-80"))}
        </div>
      </div>
    </div>
  );
};

// Mobile: three labelled groups (two columns each), always open, plus a subordinate utility row.
export const MobileNavMenu = ({ open, onClose, anchors }) => {
  const path = usePath();
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  const itemClass = "flex min-h-[48px] items-center rounded-[1px] px-2 py-1 text-[14.5px] leading-tight focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#C84B31]";
  return (
    <nav
      id="mobile-nav"
      aria-label="Services"
      data-testid="mobile-nav-panel"
      className="border-t border-[#1C1A17]/12 bg-[#F5F4F0] px-3 pb-3 pt-2 md:hidden"
      style={{ color: NAVY }}
    >
      {NAV_GROUPS.map((g) => (
        <div key={g.heading} className="pt-1" data-testid={`mobile-${g.testid}`}>
          <GroupHeading testid={`mobile-${g.testid}-heading`}>{g.heading}</GroupHeading>
          <ul className="grid grid-cols-2 gap-x-2">
            {g.links.map((l) => (
              <li key={l.href}>
                <a
                  href={l.href}
                  data-testid={`mobile-${l.testid}`}
                  aria-current={l.href === path ? "page" : undefined}
                  className={itemClass}
                  style={{ color: l.href === path ? "#C84B31" : NAVY }}
                >
                  <span>{l.label}</span>
                  {l.isNew && isFreshStartNew() && <NewBadge />}
                </a>
              </li>
            ))}
          </ul>
        </div>
      ))}
      <ul className="mt-2 flex flex-wrap items-center gap-x-1 border-t border-[#1C1A17]/12 pt-1" data-testid="mobile-nav-utility-row">
        {[...UTILITY_LINKS, ...anchors.filter((a) => !UTILITY_LINKS.some((u) => u.href.replace(/^\//, "") === a.href.replace(/^\//, "")))].map((a) => {
          const href = path === "/" && a.href.startsWith("/#") ? a.href.slice(1) : a.href;
          const onHashClick = (e) => {
            e.preventDefault();
            onClose();
            const id = href.slice(1);
            window.setTimeout(() => {
              window.history.replaceState(null, "", href);
              document.getElementById(id)?.scrollIntoView({ block: "start" });
            }, 120);
          };
          return (
          <li key={a.href}>
            <a
              href={href}
              onClick={href.startsWith("#") ? onHashClick : undefined}
              data-testid={a.testid ? `mobile-${a.testid}` : `mobile-nav-${a.href.replace(/[#/]/g, "")}`}
              aria-current={a.href === path ? "page" : undefined}
              className={`${itemClass} text-[12.5px]`}
              style={{ color: a.href === path ? "#C84B31" : SLATE }}
            >
              {a.label}
            </a>
          </li>
          );
        })}
      </ul>
    </nav>
  );
};
